import { useCallback, useEffect } from 'react';
import { Button, Group, Modal, Stack, Text } from '@mantine/core';
import { ConnectionState, type Room } from 'livekit-client';
import { useTranslation } from 'react-i18next';
import { type BlockerFunction, useBlocker } from 'react-router';

export function useLeaveGuard(room: Room) {
    const shouldBlock = useCallback<BlockerFunction>(
        ({ currentLocation, nextLocation }) =>
            room.state !== ConnectionState.Disconnected && currentLocation.pathname !== nextLocation.pathname,
        [room]
    );
    const blocker = useBlocker(shouldBlock);

    useEffect(() => {
        const confirmUnload = (event: BeforeUnloadEvent) => {
            if (room.state !== ConnectionState.Disconnected) event.preventDefault();
        };
        globalThis.addEventListener('beforeunload', confirmUnload);
        return () => {
            globalThis.removeEventListener('beforeunload', confirmUnload);
        };
    }, [room]);

    return blocker;
}

export function LeaveDialog({
    opened,
    onClose,
    onLeave,
}: Readonly<{ opened: boolean; onClose: () => void; onLeave: () => void }>) {
    const { t } = useTranslation('conference');
    return (
        <Modal opened={opened} title={t('leave.title')} onClose={onClose}>
            <Stack>
                <Text c="dimmed" size="sm">
                    {t('leave.message')}
                </Text>
                <Group justify="flex-end">
                    <Button data-autofocus variant="default" onClick={onClose}>
                        {t('leave.cancel')}
                    </Button>
                    <Button color="danger" variant="filled" onClick={onLeave}>
                        {t('leave.confirm')}
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
}
